import { useMemo, useState } from "react";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { useResource, formatDate, daysUntil } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { FilterBar, matches } from "../components/FilterBar";
import { EntityForm, Field } from "../components/EntityForm";
import { competencyCreateFields } from "../lib/formOptions";
import { ConfirmDialog } from "../components/ConfirmDialog";
import type { CompetencyType, Employee, TrainingRecord } from "../lib/types";

/** Expiry state, shared by the chips and the summary tiles. */
const stateOf = (expiry: string | null): { key: string; label: string; chip: string } => {
    if (!expiry) return { key: "none", label: "No expiry", chip: "pass" };
    const days = daysUntil(expiry);
    if (days < 0) return { key: "expired", label: "Expired", chip: "block" };
    if (days <= 30) return { key: "soon", label: `${days}d left`, chip: "warn" };
    if (days <= 60) return { key: "upcoming", label: `${days}d left`, chip: "warn" };
    return { key: "current", label: "Current", chip: "pass" };
};

type Dialog =
    | { kind: "create" }
    | { kind: "edit"; record: TrainingRecord }
    | { kind: "delete"; record: TrainingRecord; label: string }
    | { kind: "competency" }
    | null;

export const Training = () => {
    const { user } = useAuth();
    const canManage = user?.role === "admin" || user?.role === "manager";

    const training = useResource<TrainingRecord[]>("/training-records");
    const competencies = useResource<CompetencyType[]>("/competency-types");
    const employees = useResource<Employee[]>("/employees");

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [dialog, setDialog] = useState<Dialog>(null);

    const refresh = () => { training.reload(); competencies.reload(); setDialog(null); };

    const nameOf = useMemo(() => {
        const c = new Map((competencies.data ?? []).map((x) => [x.id, x.name]));
        const e = new Map((employees.data ?? []).map((x) => [x.id, x.full_name]));
        return {
            competency: (id: string) => c.get(id) ?? "Unknown competency",
            employee: (id: string) => e.get(id) ?? "Unknown",
        };
    }, [competencies.data, employees.data]);

    const employeeOptions = (employees.data ?? []).map((e) => ({ value: e.id, label: e.full_name }));
    const competencyOptions = (competencies.data ?? []).map((c) => ({ value: c.id, label: c.name }));

    const fields: Field[] = [
        { name: "employee_id", label: "Employee", kind: "select", required: true, options: employeeOptions, half: true },
        { name: "competency_type_id", label: "Competency", kind: "select", required: true, options: competencyOptions, half: true,
          addNew: { title: "New competency type", path: "/competency-types", fields: competencyCreateFields(),
              subtitle: "Remember to attach it to the roles that require it, or the rules will not enforce it." } },
        { name: "completion_date", label: "Completed on", kind: "date", required: true, half: true },
        { name: "expiry_date", label: "Expires on", kind: "date", half: true,
          help: "Leave blank for a competency that does not expire." },
        { name: "provider", label: "Training provider", kind: "select", half: true,
          optionsFrom: { path: "/training-providers", addLabel: "Add provider" } },
        { name: "assessor", label: "Assessor", kind: "select", half: true,
          optionsFrom: { path: "/training-assessors", addLabel: "Add assessor" } },
        { name: "course_name", label: "Course name", kind: "select",
          optionsFrom: { path: "/training-courses", addLabel: "Add course" } },
        { name: "evidence_url", label: "Evidence link", kind: "text" },
        { name: "notes", label: "Notes", kind: "textarea" },
    ];

    const competencyFields: Field[] = [
        { name: "name", label: "Competency name", kind: "text", required: true },
        { name: "category", label: "Category", kind: "text", half: true },
        { name: "default_valid_months", label: "Valid for (months)", kind: "number", min: 1, half: true,
          help: "Leave blank if it does not expire." },
        { name: "description", label: "Description", kind: "textarea" },
    ];

    const all = training.data ?? [];
    const filtered = all.filter((record) =>
        matches(search, nameOf.employee(record.employee_id), nameOf.competency(record.competency_type_id), record.provider) &&
        (!filters.employee_id || record.employee_id === filters.employee_id) &&
        (!filters.competency_type_id || record.competency_type_id === filters.competency_type_id) &&
        (!filters.state || stateOf(record.expiry_date).key === filters.state)
    );

    const tiles = [
        { label: "Expired", key: "expired", tone: "var(--block)" },
        { label: "Due within 30 days", key: "soon", tone: "var(--warn)" },
        { label: "Current", key: "current", tone: "var(--pass)" },
    ].map((t) => ({ ...t, count: all.filter((r) => stateOf(r.expiry_date).key === t.key).length }));

    if (training.error) return <div className="error-banner">{training.error}</div>;

    return (
        <div>
            {dialog?.kind === "create" && (
                <EntityForm title="Add Training Record" path="/training-records" fields={fields}
                    subtitle="Recording a mandatory competency is what unblocks rostering for roles that require it."
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "edit" && (
                <EntityForm title="Edit training record" path="/training-records"
                    fields={fields} initial={dialog.record}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "competency" && (
                <EntityForm title="Add competency type" path="/competency-types" fields={competencyFields}
                    subtitle="Competency types are the catalogue. Linking one to a job role as mandatory is what makes it block rostering."
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "delete" && (
                <ConfirmDialog
                    title="Delete this training record?"
                    tone="danger"
                    message={dialog.label}
                    warning="If this competency is mandatory for a role the employee holds, deleting the evidence will immediately block them from being rostered. That is the correct behaviour — but check it is what you intend."
                    confirmLabel="Delete record"
                    request={{ method: "delete", path: `/training-records/${dialog.record.id}` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}

            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search training records…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[
                    { name: "employee_id", allLabel: "All Employees", options: employeeOptions },
                    { name: "competency_type_id", allLabel: "All Competencies", options: competencyOptions },
                    { name: "state", allLabel: "All Status", options: [
                        { value: "expired", label: "Expired" },
                        { value: "soon", label: "Due within 30 days" },
                        { value: "upcoming", label: "Due within 60 days" },
                        { value: "current", label: "Current" },
                        { value: "none", label: "No expiry" },
                    ] },
                ]}
                resultNote={filtered.length !== all.length ? `Showing ${filtered.length} of ${all.length} records` : undefined}
                action={canManage && (
                    <div style={{ display: "flex", gap: "0.4rem" }}>
                        <button className="btn ghost" onClick={() => setDialog({ kind: "competency" })}>+ Competency type</button>
                        <button className="btn" onClick={() => setDialog({ kind: "create" })}>+ Add Training</button>
                    </div>
                )}
            />

            <div className="grid cols-3" style={{ marginBottom: "1rem" }}>
                {tiles.map((tile) => (
                    <div key={tile.key} className="card">
                        <div className="stat">
                            <div className="label">{tile.label}</div>
                            <div className="value" style={{ color: tile.tone }}>{tile.count}</div>
                            <div className="sub">{tile.count === 1 ? "record" : "records"}</div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Employee</th><th>Competency</th><th>Provider</th>
                                    <th>Completed</th><th>Expires</th><th>Status</th>
                                    {canManage && <th style={{ textAlign: "right" }}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((record) => {
                                    const state = stateOf(record.expiry_date);
                                    return (
                                        <tr key={record.id}>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                                    <InitialsAvatar name={nameOf.employee(record.employee_id)} size={26} />
                                                    {nameOf.employee(record.employee_id)}
                                                </div>
                                            </td>
                                            <td>{nameOf.competency(record.competency_type_id)}</td>
                                            <td>{record.provider ?? "—"}</td>
                                            <td className="mono">{formatDate(record.completion_date)}</td>
                                            <td className="mono">{formatDate(record.expiry_date)}</td>
                                            <td><span className={`chip ${state.chip}`}>{state.label}</span></td>
                                            {canManage && (
                                                <td>
                                                    <div className="row-actions">
                                                        <button className="icon-btn" onClick={() => setDialog({ kind: "edit", record })}>Edit</button>
                                                        <button className="icon-btn danger" onClick={() => setDialog({
                                                            kind: "delete", record,
                                                            label: `${nameOf.competency(record.competency_type_id)} for ${nameOf.employee(record.employee_id)}.`,
                                                        })}>Delete</button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={canManage ? 7 : 6} className="empty">No training records match.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};
